<script lang="ts">
    import type {ViewedContentPageData} from '../../../../lib/mv-viewed-content';
    import type * as Pdfjs from 'pdfjs-dist';
    import {onDestroy, onMount} from 'svelte';
    import {getThemeContext} from 'shared/theme/portaro-theme';
    import {getViewedContentContext} from '../../../../lib/mv-viewed-content';
    import {fade} from 'svelte/transition';
    import {exists} from 'shared/utils/custom-utils';
    import {GO_TO_PAGE_EVENT} from '../../../../lib/mv-constants';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';

    export let page: Pdfjs.PDFPageProxy;
    const size = 96;

    const viewedContent = getViewedContentContext();
    const currentTheme = getThemeContext().currentTheme;
    const renderingFadeAnimParams = {duration: 125};
    let pagePreviewCanvas: HTMLCanvasElement;
    let observer: IntersectionObserver;
    let rendered = false;
    let pagePreviewElement: HTMLDivElement;
    let pageData: ViewedContentPageData;
    let renderTask: Pdfjs.RenderTask | null = null;
    const pageDataUnsubscribe = viewedContent.pageData.subscribe((currentPageData) => pageData = currentPageData);

    $: rendering = !rendered || exists(renderTask);

    onMount(() => {
        setupCanvas();

        // Initialize observer to only re-render visible pages
        observer = new IntersectionObserver((entries) => {
            const intersecting = entries.some((entry) => entry.isIntersecting);

            if (!intersecting || rendered) {
                return;
            }

            renderThumbnailPage().then(() => {
                renderTask = null;
                rendered = true;
            });
        }, {
            root: null,
            rootMargin: '100px',
            threshold: 0
        });

        if (exists(pagePreviewElement)) {
            observer.observe(pagePreviewElement);
        }
    });

    onDestroy(() => {
        if (exists(observer)) {
            observer.disconnect();
        }

        if (exists(renderTask)) {
            renderTask.cancel();
        }

        pageDataUnsubscribe();
    });

    function setupCanvas() {
        const pageViewport = page.getViewport({scale: 1});

        pagePreviewCanvas.width = size;
        pagePreviewCanvas.height = size * (pageViewport.height / pageViewport.width);
    }

    async function renderThumbnailPage(): Promise<void> {
        const pageViewport = page.getViewport({scale: 1});
        const scale = Math.min(pagePreviewCanvas.width / pageViewport.width, pagePreviewCanvas.height / pageViewport.height);

        renderTask = page.render({
            canvasContext: pagePreviewCanvas.getContext('2d'),
            viewport: page.getViewport({scale})
        });

        return renderTask.promise;
    }

    const handlePageClick = () => {
        const totalPages = pageData?.total ?? 1;
        viewedContent.setPageData({
            current: page.pageNumber,
            total: totalPages,
            pagesShown: pageData?.pagesShown ?? false
        });
        viewedContent.eventBus.dispatchEvent(new CustomEvent<number>(GO_TO_PAGE_EVENT, {detail: page.pageNumber}));
    }
</script>

<div class="kp-pdf-page-preview" bind:this={pagePreviewElement}>
    <button class="page-container {$currentTheme}"
            class:current-page={pageData.current === page.pageNumber}
            on:click={handlePageClick}>

        {#if rendering}
            <div class="rendering-loader-container" transition:fade={renderingFadeAnimParams}>
                <KpLoadingBlock size="sm"/>
            </div>
        {/if}

        <canvas bind:this={pagePreviewCanvas}/>
    </button>

    <span class="page-number">{page.pageNumber}</span>
</div>

<style lang="less">
    @import (reference) "src/features/media-viewer/media-viewer.variables.less";

    .kp-pdf-page-preview {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: @spacing-s;

        .page-number {
            bottom: @spacing-sm;
            font-size: @font-size-small;
            text-align: center;
            user-select: none;
            text-shadow: rgba(0, 0, 0, 0.25) 0 2px 20px;
        }

        .page-container {
            position: relative;
            display: flex;
            flex-shrink: 0;
            border-radius: @border-radius-default;
            border: 1px solid var(--viewer-default-border);
            cursor: pointer;
            padding: 0;
            margin: 0;
            overflow: hidden;
            outline: 2px solid transparent;
            outline-offset: 1px;
            background: none;
            transition: opacity 0.3s ease-in-out, outline-color 0.3s ease-in-out;

            &.light {
                box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.07);
            }

            &:hover,
            &.current-page {
                outline-color: var(--brand-orange-new);
            }

            &:hover {
                opacity: 0.5;
            }

            .rendering-loader-container {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                z-index: 1;
            }

            canvas {
                background-color: var(--viewer-bg);
            }
        }
    }
</style>